import {
  type EquipmentBrand,
  useGetBrandsQuery,
  useSetBrandStatusMutation,
} from '@/services/equipmentManufacturers';
import { isErrorWithData } from '@/services/helpers';
import { PalmettoButton, PalmettoIcon, PalmettoModal, PalmettoSelect } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EquipmentRecordStatus } from 'types';

import { EquipmentBrandForm } from './EquipmentBrandForm';
import { EquipmentBrandRow } from './EquipmentBrandRow';
import { brandPageLimit, enumParam, statusOptions, statusValues } from './equipmentLabels';

/** Sentinel for "the add form is the open editor", distinct from any brand id. */
const newBrandKey = 'new';

const addIcon = <PalmettoIcon name="add" size="sm" />;

export interface EquipmentBrandsSectionProps {
  manufacturerId: string;
  canEdit: boolean;
}

/**
 * Brands are a separate collection, so unlike the embedded checklist items this borrows its
 * interaction model from, every add, edit, re-map, and status change is its own request and the
 * list refetches through cache invalidation rather than local array surgery.
 */
export const EquipmentBrandsSection = ({ manufacturerId, canEdit }: EquipmentBrandsSectionProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status =
    enumParam<EquipmentRecordStatus>(searchParams.get('brandStatus'), statusValues) || EquipmentRecordStatus.active;

  const { data, isLoading, isError } = useGetBrandsQuery({ manufacturerId, status, limit: brandPageLimit });
  const [setBrandStatus, { isLoading: isChangingStatus }] = useSetBrandStatusMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<EquipmentBrand | null>(null);

  const brands = data?.data ?? [];
  const isEditingAny = editingId !== null;

  const handleStatusChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('brandStatus', value);
      else next.delete('brandStatus');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleAdd = useCallback(() => setEditingId(newBrandKey), []);
  const handleEdit = useCallback((brand: EquipmentBrand) => setEditingId(brand.id), []);
  const handleEditorDone = useCallback(() => setEditingId(null), []);

  const handleDeactivateRequest = useCallback((brand: EquipmentBrand) => setPendingDeactivate(brand), []);
  const handleDeactivateCancel = useCallback(() => setPendingDeactivate(null), []);

  const handleDeactivateConfirm = useCallback(async () => {
    if (!pendingDeactivate) return;
    try {
      await setBrandStatus({
        id: pendingDeactivate.id,
        manufacturerId,
        status: EquipmentRecordStatus.inactive,
      }).unwrap();
      toast.success(`${pendingDeactivate.name} deactivated.`);
      setPendingDeactivate(null);
    } catch (error) {
      console.error('Error deactivating equipment brand:', error);
      toast.error(isErrorWithData(error) ? error.data.message : 'Failed to deactivate brand.');
    }
  }, [pendingDeactivate, setBrandStatus, manufacturerId]);

  const handleActivate = useCallback(
    async (brand: EquipmentBrand) => {
      try {
        await setBrandStatus({ id: brand.id, manufacturerId, status: EquipmentRecordStatus.active }).unwrap();
        toast.success(`${brand.name} activated.`);
      } catch (error) {
        console.error('Error activating equipment brand:', error);
        toast.error(isErrorWithData(error) ? error.data.message : 'Failed to activate brand.');
      }
    },
    [setBrandStatus, manufacturerId],
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <h2 className="text-left heading-200 text-text-primary">Brands</h2>
        <div className="flex items-end gap-8">
          <PalmettoSelect
            label="Status"
            items={statusOptions.map((opt) => ({ ...opt, selected: opt.value === status }))}
            onValueChange={handleStatusChange}
          />
          {canEdit && (
            <PalmettoButton
              type="button"
              emphasis="secondary"
              layout="hug"
              leadingIcon={addIcon}
              onClick={handleAdd}
              disabled={isEditingAny}
            >
              Add Brand
            </PalmettoButton>
          )}
        </div>
      </div>

      {editingId === newBrandKey && <EquipmentBrandForm manufacturerId={manufacturerId} onDone={handleEditorDone} />}

      {isError ? (
        <div className="rounded-8 border border-border-default p-24 text-center text-text-secondary">
          Failed to load brands.
        </div>
      ) : (
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-border-default body-bold-200 text-text-secondary">
              <th className="p-8">Name</th>
              <th className="p-8">Website</th>
              <th className="p-8">Warranty Registration</th>
              <th className="p-8">Status</th>
              <th className="p-8">Last Modified</th>
              <th className="p-8" />
            </tr>
          </thead>
          <tbody>
            {brands.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="p-24 text-center text-text-secondary">
                  No brands.
                </td>
              </tr>
            )}
            {brands.map((brand) =>
              editingId === brand.id ? (
                <tr key={brand.id}>
                  <td colSpan={6} className="p-8">
                    <EquipmentBrandForm manufacturerId={manufacturerId} brand={brand} onDone={handleEditorDone} />
                  </td>
                </tr>
              ) : (
                <EquipmentBrandRow
                  key={brand.id}
                  brand={brand}
                  canEdit={canEdit}
                  isEditingAny={isEditingAny}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivateRequest}
                  onActivate={handleActivate}
                />
              ),
            )}
          </tbody>
        </table>
      )}

      {pendingDeactivate && (
        <PalmettoModal
          open
          onClose={handleDeactivateCancel}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleDeactivateCancel}
                disabled={isChangingStatus}
              >
                Cancel
              </PalmettoButton>
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleDeactivateConfirm}
                disabled={isChangingStatus}
              >
                Deactivate
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-16">
            <h2>Deactivate {pendingDeactivate.name}?</h2>
            <p className="text-text-secondary">
              It stays attached to historical packages and can be activated again later. Its manufacturer and sibling
              brands are unaffected.
            </p>
          </div>
        </PalmettoModal>
      )}
    </div>
  );
};
